import { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { Palette, Route, Calendar, Database, Sparkles } from "lucide-react";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { BrandingCard } from "./BrandingCard";
import { RouteCard } from "./RouteCard";
import { DayPlannerCard } from "./DayPlannerCard";
import { ItemManagerCard } from "./ItemManagerCard";

const features = [
	{
		id: 1,
		icon: Palette,
		title: "Set Up Your Branding",
		description:
			"Customize colors, logos, and fonts to match your travel agency's unique identity. Upload your brand assets and watch as Stippl automatically applies them across all your itineraries.",
		details: ["Custom color schemes", "Logo placement", "Font selection", "Brand templates"],
		image:
			"https://images.unsplash.com/photo-1689307300540-e3a2313cd6a3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxicmFuZGluZyUyMGRlc2lnbiUyMGNvbG9yZnVsfGVufDF8fHx8MTc3Mzc2MTk1MHww&ixlib=rb-4.1.0&q=80&w=1080",
		position: { x: 0, y: 0 },
	},
	{
		id: 2,
		icon: Route,
		title: "Build Your Route",
		description:
			"Plot your journey on an interactive map. Add destinations, create custom routes, and visualize the entire travel path. Connect locations in the perfect order for your clients.",
		details: ["Interactive mapping", "Route optimization", "Distance tracking", "Multi-stop planning"],
		image:
			"https://images.unsplash.com/photo-1758928807778-3e09d91b83b9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyb3V0ZSUyMG1hcCUyMHBsYW5uaW5nJTIwZGVzdGluYXRpb25zfGVufDF8fHx8MTc3Mzc2NTI0N3ww&ixlib=rb-4.1.0&q=80&w=1080",
		position: { x: 850, y: 20 },
	},
	{
		id: 3,
		icon: Calendar,
		title: "Plan Your Days",
		description: "Schedule activities, transportation, and accommodations for each day. Build the perfect itinerary.",
		details: ["Daily scheduling", "Calendar view", "Time blocking", "Activity planning"],
		image:
			"https://images.unsplash.com/photo-1657040298726-7189d3090d5e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0cmF2ZWwlMjBpdGluZXJhcnklMjBwbGFubmluZyUyMGNhbGVuZGFyfGVufDF8fHx8MTc3Mzc2NTI0OHww&ixlib=rb-4.1.0&q=80&w=1080",
		position: { x: 1550, y: -100 },
	},
	{
		id: 4,
		icon: Database,
		title: "Manage Your Items",
		description:
			"Keep track of all bookings, accommodations, transportation, and activities. Organize everything in one central database with confirmation numbers and important details.",
		details: ["Booking management", "Item database", "Confirmation tracking", "Notes & details"],
		image:
			"https://images.unsplash.com/photo-1673515335048-ace62cf73a26?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxvcmdhbml6ZWQlMjB0cmF2ZWwlMjBpdGVtcyUyMGNoZWNrbGlzdHxlbnwxfHx8fDE3NzM3NjUyNDh8MA&ixlib=rb-4.1.0&q=80&w=1080",
		position: { x: 2250, y: -100 },
	},
	{
		id: "core-planner" as const,
		icon: Sparkles,
		title: "Core Planner",
		description: "Creating magic for your travel business",
		details: [],
		image: "",
		position: { x: 2950, y: 0 },
	},
];

export function FeaturesFlow() {
	const containerRef = useRef<HTMLDivElement>(null);
	const [scrollProgress, setScrollProgress] = useState(0);

	useEffect(() => {
		const handleScroll = () => {
			if (!containerRef.current) return;

			const rect = containerRef.current.getBoundingClientRect();
			const progress = -rect.top / (rect.height - window.innerHeight);
			const clampedProgress = Math.max(0, Math.min(1, progress));

			setScrollProgress(clampedProgress);
		};

		window.addEventListener("scroll", handleScroll);
		handleScroll();

		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (
		<div
			ref={containerRef}
			className="relative"
			style={{
				height: "400vh",
				background: "#ffffff",
			}}
		>
			<div className="sticky top-0 h-screen flex flex-col items-center justify-start overflow-hidden pt-16">
				<div className="w-full max-w-7xl mx-auto px-6">
					{/* Section Header */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6 }}
						className="text-center mb-20 z-20"
					>
						<p className="text-sm font-semibold tracking-wide mb-3 uppercase" style={{ color: "#8B5CF6" }}>
							THE PLANNER
						</p>
						<h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ color: "#0F3E4A", fontFamily: "Poppins, sans-serif" }}>
							Easier than you think.<br />
							More powerful than you'd expect.
						</h2>
					</motion.div>

					{/* Features Flow - horizontal scrolling effect */}
					<div className="relative h-[600px]">
						{/* Dotted lines connecting cards */}
						<svg className="absolute inset-0 w-full h-full pointer-events-none z-0" style={{ overflow: "visible" }}>
							{features.slice(0, -1).map((feature, index) => {
								const nextFeature = features[index + 1];
								const startProgress = index / (features.length - 1);
								const endProgress = (index + 1) / (features.length - 1);
								const pathProgress = Math.max(
									0,
									Math.min(1, (scrollProgress - startProgress) / (endProgress - startProgress))
								);

								// Calculate positions
								const startX = feature.position.x + 275;
								const endX = nextFeature.position.x + 275;
								const midX = (startX + endX) / 2;
								const startY = feature.position.y + 250;
								const endY = nextFeature.position.y + 250;

								// Create a curved path
								const path = `M ${startX},${startY} Q ${midX},${Math.min(startY, endY) - 50} ${endX},${endY}`;

								// Calculate line opacity - fade out as next card appears
								const lineOpacity =
									index === features.length - 2
										? Math.max(0, 0.4 - (scrollProgress - 0.85) * 2) // Fade out the last line as Core Planner appears
										: 0.4;

								return (
									<motion.path
										key={`path-${feature.id}`}
										d={path}
										fill="none"
										stroke="#A78BFA"
										strokeWidth="6"
										strokeDasharray="12 12"
										strokeLinecap="round"
										style={{
											transform: `translateX(${-scrollProgress * 2850}px)`,
											opacity: lineOpacity,
										}}
										initial={{ pathLength: 0 }}
										animate={{ pathLength: pathProgress }}
										transition={{ duration: 0.5 }}
									/>
								);
							})}
						</svg>

						{features.map((feature, index) => {
							const featureProgress = index / (features.length - 1);
							const rawOpacity = (scrollProgress - (featureProgress - 0.1)) / 0.2;
							const opacity = Math.max(0, Math.min(1, isNaN(rawOpacity) ? 0 : rawOpacity));
							const Icon = feature.icon;

							// Adjust margin based on card width or special cards
							let cardWidth = 550;
							if (feature.id === 2) cardWidth = 900;
							if (feature.id === 4) cardWidth = 650;
							if (feature.id === "core-planner") cardWidth = 400;

							const marginLeft = -(cardWidth / 2);

							// Special positioning for Core Planner - center it better
							let marginTop = "-200px";
							if (feature.id === "core-planner") marginTop = "0px";

							// Calculate purple intensity for Core Planner based on scroll
							const purpleIntensity =
								feature.id === "core-planner" ? Math.min(1, Math.max(0, (scrollProgress - 0.85) / 0.1)) : 0;

							// Calculate zoom scale for Core Planner - zooms in as it scrolls into view
							const zoomScale = feature.id === "core-planner"
								? 1 + opacity * 0.4 // Zoom from 1x to 1.4x as card appears
								: 1;

							return (
								<motion.div
									key={feature.id}
									className="absolute"
									style={{
										left: "50%",
										top: "50%",
										x: feature.position.x - scrollProgress * 2850,
										y: feature.position.y,
										marginLeft: `${marginLeft}px`,
										marginTop: marginTop,
										opacity: feature.id === "core-planner" ? 1 : opacity, // Core Planner always 100% opacity
										scale: zoomScale,
									}}
								>
									{feature.id === 1 ? (
										<BrandingCard />
									) : feature.id === 2 ? (
										<RouteCard />
									) : feature.id === 3 ? (
										<DayPlannerCard />
									) : feature.id === 4 ? (
										<ItemManagerCard />
									) : feature.id === "core-planner" ? (
										<div className="relative">
											{/* Multiple glow layers for depth */}
											<div
												className="absolute inset-0 blur-3xl animate-pulse"
												style={{
													background: "radial-gradient(circle at 50% 50%, #A78BFA 0%, #C084FC 40%, transparent 70%)",
													animation: "pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite",
													opacity: isNaN(purpleIntensity) ? 0.4 : 0.4 + purpleIntensity * 0.3,
												}}
											/>
											<div
												className="absolute inset-0 blur-2xl"
												style={{
													background: "radial-gradient(circle at 30% 30%, #8B5CF6 0%, transparent 60%)",
													opacity: isNaN(purpleIntensity) ? 0.3 : 0.3 + purpleIntensity * 0.4,
												}}
											/>

											{/* Magical card container */}
											<div className="relative">
												{/* Floating sparkles around the card */}
												<motion.div
													className="absolute -top-8 -left-8 text-3xl"
													animate={{
														scale: [1, 1.4, 1],
														rotate: [0, 20, 0],
														y: [0, -10, 0],
													}}
													transition={{
														duration: 2.5,
														repeat: Infinity,
														ease: "easeInOut",
													}}
												>
													✨
												</motion.div>
												<motion.div
													className="absolute -top-4 -right-12 text-2xl"
													animate={{
														scale: [1, 1.3, 1],
														rotate: [0, -15, 0],
														x: [0, 5, 0],
													}}
													transition={{
														duration: 2,
														repeat: Infinity,
														delay: 0.5,
														ease: "easeInOut",
													}}
												>
													⭐
												</motion.div>
												<motion.div
													className="absolute -bottom-6 -left-10 text-xl"
													animate={{
														scale: [1, 1.5, 1],
														rotate: [0, 25, 0],
														y: [0, 8, 0],
													}}
													transition={{
														duration: 2.8,
														repeat: Infinity,
														delay: 0.8,
														ease: "easeInOut",
													}}
												>
													💫
												</motion.div>
												<motion.div
													className="absolute -bottom-8 -right-8 text-2xl"
													animate={{
														scale: [1, 1.2, 1],
														rotate: [0, -20, 0],
														x: [0, -6, 0],
													}}
													transition={{
														duration: 2.3,
														repeat: Infinity,
														delay: 0.3,
														ease: "easeInOut",
													}}
												>
													✨
												</motion.div>
												<motion.div
													className="absolute top-1/2 -left-16 text-xl"
													animate={{
														scale: [1, 1.6, 1],
														rotate: [0, 30, 0],
														opacity: [0.6, 1, 0.6],
													}}
													transition={{
														duration: 2.6,
														repeat: Infinity,
														delay: 0.6,
														ease: "easeInOut",
													}}
												>
													🌟
												</motion.div>
												<motion.div
													className="absolute top-1/3 -right-14 text-lg"
													animate={{
														scale: [1, 1.4, 1],
														rotate: [0, -25, 0],
														opacity: [0.7, 1, 0.7],
													}}
													transition={{
														duration: 2.4,
														repeat: Infinity,
														delay: 1,
														ease: "easeInOut",
													}}
												>
													✨
												</motion.div>

												{/* Main magical card */}
												<motion.div
													className="relative rounded-2xl shadow-2xl overflow-hidden"
													style={{
														background: "#6D28D9",
													}}
													animate={{
														boxShadow: [
															"0 25px 50px -12px rgba(139, 92, 246, 0.5)",
															"0 25px 50px -12px rgba(167, 139, 250, 0.7)",
															"0 25px 50px -12px rgba(139, 92, 246, 0.5)",
														],
													}}
													transition={{
														duration: 3,
														repeat: Infinity,
														ease: "easeInOut",
													}}
												>
													{/* Solid opaque background layer */}
													<div className="absolute inset-0 z-0" style={{ background: "#6D28D9", opacity: 1 }} />

													{/* Animated gradient background */}
													<motion.div
														className="absolute inset-0 z-10"
														style={{
															background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%)",
															opacity: 1,
														}}
														animate={{
															background: [
																"linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%)",
																"linear-gradient(135deg, #A78BFA 0%, #8B5CF6 50%, #7C3AED 100%)",
																"linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%)",
															],
														}}
														transition={{
															duration: 4,
															repeat: Infinity,
															ease: "easeInOut",
														}}
													/>

													{/* Shimmer overlay */}
													<motion.div
														className="absolute inset-0 z-20"
														style={{
															background: "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)",
														}}
														animate={{
															x: ["-100%", "200%"],
														}}
														transition={{
															duration: 3,
															repeat: Infinity,
															ease: "linear",
															repeatDelay: 1,
														}}
													/>

													{/* Content */}
													<div className="relative px-8 py-6 z-30">
														<div className="flex items-center gap-4">
															{/* Magic Wand with intense sparkles */}
															<div className="relative text-5xl">
																<motion.span
																	animate={{
																		rotate: [0, 15, -15, 0],
																	}}
																	transition={{
																		duration: 2,
																		repeat: Infinity,
																		ease: "easeInOut",
																	}}
																>
																	🪄
																</motion.span>

																{/* Multiple orbiting sparkles */}
																<motion.span
																	className="absolute -top-3 -right-3 text-2xl"
																	animate={{
																		scale: [1, 1.5, 1],
																		rotate: [0, 360],
																		opacity: [0.8, 1, 0.8],
																	}}
																	transition={{
																		duration: 2,
																		repeat: Infinity,
																	}}
																>
																	✨
																</motion.span>
																<motion.span
																	className="absolute -bottom-2 -left-2 text-xl"
																	animate={{
																		scale: [1, 1.4, 1],
																		rotate: [0, -360],
																		opacity: [0.7, 1, 0.7],
																	}}
																	transition={{
																		duration: 2.5,
																		repeat: Infinity,
																		delay: 0.5,
																	}}
																>
																	✨
																</motion.span>
																<motion.span
																	className="absolute top-0 right-10 text-lg"
																	animate={{
																		scale: [1, 1.6, 1],
																		y: [0, -8, 0],
																		opacity: [0.6, 1, 0.6],
																	}}
																	transition={{
																		duration: 1.8,
																		repeat: Infinity,
																		delay: 0.3,
																	}}
																>
																	⭐
																</motion.span>
																<motion.span
																	className="absolute bottom-2 right-6 text-base"
																	animate={{
																		scale: [1, 1.3, 1],
																		rotate: [0, 180, 360],
																		opacity: [0.8, 1, 0.8],
																	}}
																	transition={{
																		duration: 2.2,
																		repeat: Infinity,
																		delay: 0.7,
																	}}
																>
																	💫
																</motion.span>
															</div>

															{/* Text with glow */}
															<div className="text-white">
																<motion.div
																	className="text-2xl font-bold mb-1"
																	style={{
																		textShadow: "0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.5)",
																	}}
																	animate={{
																		textShadow: [
																			"0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.5)",
																			"0 0 30px rgba(255, 255, 255, 1), 0 0 50px rgba(255, 255, 255, 0.7)",
																			"0 0 20px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.5)",
																		],
																	}}
																	transition={{
																		duration: 2.5,
																		repeat: Infinity,
																		ease: "easeInOut",
																	}}
																>
																	Create with ease. Impress every time.
																</motion.div>
																<div className="text-base opacity-90 leading-relaxed mt-2">Create magic for your travel business.</div>
															</div>
														</div>
													</div>
												</motion.div>
											</div>
										</div>
									) : (
										<div
											className="w-[550px] rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 hover:scale-105 relative bg-white"
											style={{
												border: "2px solid rgba(0, 212, 170, 0.3)",
											}}
										>
											{/* Glow effect */}
											<div
												className="absolute inset-0 opacity-10 blur-xl"
												style={{
													background: "radial-gradient(circle at 50% 0%, #00D4AA 0%, transparent 70%)",
												}}
											/>

											{/* Image with gradient overlay */}
											<div className="relative h-56 overflow-hidden">
												<ImageWithFallback src={feature.image} alt={feature.title} className="w-full h-full object-cover" />
												<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

												{/* Icon badge */}
												<div
													className="absolute top-5 right-5 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg backdrop-blur-sm"
													style={{
														background: "rgba(0, 212, 170, 0.9)",
														border: "2px solid #00D4AA",
													}}
												>
													<Icon className="w-7 h-7 text-white" />
												</div>

												{/* Number badge */}
												<div
													className="absolute top-5 left-5 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
													style={{
														background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
													}}
												>
													{feature.id}
												</div>
											</div>

											{/* Content */}
											<div className="p-7 relative z-10">
												<h3 className="text-2xl font-bold mb-3 text-gray-900">{feature.title}</h3>
												<p className="text-gray-600 text-sm leading-relaxed mb-5">{feature.description}</p>

												{/* Feature details */}
												<div className="grid grid-cols-2 gap-2">
													{feature.details.map((detail, idx) => (
														<div
															key={idx}
															className="flex items-center gap-2 text-sm text-gray-700 py-1.5 px-3 rounded-lg"
															style={{
																background: "rgba(0, 212, 170, 0.08)",
																border: "1px solid rgba(0, 212, 170, 0.2)",
															}}
														>
															<div className="w-1.5 h-1.5 rounded-full bg-[#00D4AA]" />
															<span>{detail}</span>
														</div>
													))}
												</div>
											</div>
										</div>
									)}
								</motion.div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
}
